import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { integrationQueryKeys } from './integrationQueryKeys';
import {
  getIntegrationDisplayNamesApi,
  IntegrationDisplayName,
} from 'IntegrationModule/api/getIntegrationDisplayNamesApi';
import { integrations, integrationsHash } from '@constants/integration';
import { AxiosResponse } from 'axios';
import { useMemo } from 'react';

/** Fallback built from hardcoded constants when API is unavailable. */
const FALLBACK_DISPLAY_NAMES: IntegrationDisplayName[] = integrations
  .map((i) => ({ displayName: i.label, nonDisplayName: i.id }))
  .sort((a, b) => a.displayName.localeCompare(b.displayName));

/**
 * Fetches integration display names from GET /service/integrationDisplayNames.
 *
 * Returns `{ displayName, nonDisplayName }[]` where:
 * - `displayName` is the human-readable label (e.g., "Jira Cloud")
 * - `nonDisplayName` is the integration identifier (e.g., "jira-cloud")
 *
 * Falls back to hardcoded constants when the API is unavailable.
 */
export const useIntegrationDisplayNamesQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const query = useAppQuery<AxiosResponse<IntegrationDisplayName[] | string[]>>({
    queryKey: integrationQueryKeys.list({
      identifier: 'useIntegrationDisplayNamesQuery',
    }),
    queryFn: ({ token, buildConfig }) =>
      getIntegrationDisplayNamesApi({
        params: undefined,
        token,
        config: buildConfig(),
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes — display names change infrequently
    enabled,
  });

  const integrationDisplayNames: IntegrationDisplayName[] = useMemo(() => {
    const raw = query.data?.data;
    if (!Array.isArray(raw) || raw.length === 0) {
      return query.isError ? FALLBACK_DISPLAY_NAMES : [];
    }

    // Normalize: the endpoint may return { displayName, nonDisplayName }[] or
    // plain string[] depending on the backend version. Handle both.
    const normalized: IntegrationDisplayName[] =
      typeof raw[0] === 'string'
        ? (raw as string[]).map((name) => ({
            displayName: integrationsHash[name]?.label ?? name,
            nonDisplayName: name,
          }))
        : (raw as IntegrationDisplayName[]);

    // Deduplicate by nonDisplayName
    const seen = new Set<string>();
    const deduped = normalized.filter((item) => {
      if (seen.has(item.nonDisplayName)) return false;
      seen.add(item.nonDisplayName);
      return true;
    });

    // Sort alphabetically by display name
    return deduped.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [query.data?.data, query.isError]);

  const integrationDisplayNamesHash = useMemo(
    () =>
      Object.fromEntries(
        integrationDisplayNames.map((item) => [item.nonDisplayName, item]),
      ) as Record<string, IntegrationDisplayName>,
    [integrationDisplayNames],
  );

  return {
    integrationDisplayNames,
    integrationDisplayNamesHash,
    isLoading: query.isLoading,
  };
};
